'use client';

import type { ProductUnitItem } from '@weberus/shared-types';
import { useHashModalAppRouter } from '@weberus/next-libs/feature/hash-modal-app-router';
import { MODAL__SWIPE_BACK__OPTION } from '@weberus/ui/utils/swipe-back-options';
import { generateHashedId } from '@weberus/utils';
import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { FullScreenModal } from '@weberus/ui/components/fullscreen-modal';
import { MapButton } from 'app/_components/feature/map-button';
import { useUniversalLocalMapQuery } from '@/app/(pages)/results/api/queries';
import { PlaceMapPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-accommodations-korea-web-txtpb';
import { LocalMapLayout } from '@/app/(pages)/results/(pages)/ui/local-map-modal/local-map';
import { deviceAtom } from '@/app/_shared/store/device';
import { ProductListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-web-txtpb';

export const LocalMapModalButton = () => {
  const { deviceType } = useAtomValue(deviceAtom);

  const { isError, placeList, serverLogMeta } = useUniversalLocalMapQuery();

  const [mapModalOpen, setMapModalOpen] = useHashModalAppRouter({
    id: `hash-${generateHashedId('list-map')}`,
    swipeBackOption: MODAL__SWIPE_BACK__OPTION,
  });

  useEffect(() => {
    if (mapModalOpen) {
      MODAL__SWIPE_BACK__OPTION.onOpen(); // 모달이 열려있을 때는 swipe back을 막는다.

      const rootModal = document.getElementById('_ROOT_MODAL_');
      if (rootModal) {
        rootModal.style.zIndex = '30';
      }
    } else {
      MODAL__SWIPE_BACK__OPTION.onClose(); // 모달이 닫혔을 때는 swipe back을 허용한다.
    }
  }, [mapModalOpen]);

  const handleImpr = () => {
    const logMeta = {
      listName: '',
      serverLogMeta,
    };
    clientLogProtobuf(PlaceMapPageLog.ListName.view, logMeta)();
  };

  const handleImprPlace = (place: ProductUnitItem) => {
    const log = {
      placeNo: place.data.id,
      listName: '',
      logMeta: place.serverLogMeta,
    };
    clientLogProtobuf(PlaceMapPageLog.ListNamePlaceNo.impression, log)();
  };

  const handleClickPlace = (place: ProductUnitItem) => {
    const log = {
      placeNo: place.data.id,
      listName: '',
      logMeta: place.serverLogMeta,
    };
    clientLogProtobuf(PlaceMapPageLog.ListNamePlaceNo.click, log)();
  };

  const handleClickMoreButton = () => {
    clientLogProtobuf(PlaceMapPageLog.ListNameButtonSeeMore.click, {
      listName: '',
      logMeta: serverLogMeta,
    })();
  };

  // 네트워크 에러 및 조회된 숙소가 없으면 지도 버튼을 노출하지 않음
  if (isError || !placeList.length || (placeList.length === 1 && !Array.isArray(placeList[0]))) {
    return null;
  }

  return (
    <>
      <MapButton
        title="지도"
        onClick={() => {
          setMapModalOpen(true);
          clientLogProtobuf(ProductListPageLog.SearchButtonSetMapview.click, {
            logMeta: serverLogMeta,
          })();
        }}
      />
      <FullScreenModal isOpened={mapModalOpen} zIndex={30}>
        <LocalMapLayout
          deviceType={deviceType}
          onClose={() => setMapModalOpen(false)}
          onImpression={handleImpr}
          onImpressionPlace={handleImprPlace}
          onClickPlace={handleClickPlace}
          onClickMoreButton={handleClickMoreButton}
        />
      </FullScreenModal>
    </>
  );
};
